import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faHandHoldingUsd,
  faCreditCard,
  faLandmark,
  faUserCircle,
  faArrowRightArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import * as Sentry from '@sentry/react';

import { WebSocketContextProvider } from 'hooks/useWebSocket';
import { GTMContextProvider } from '@ui/hooks/useGTM';
import { CustomerIoContextProvider } from '@ui/hooks/useCustomerIo';
import { FacebookPixelContextProvider } from '@ui/hooks/useFacebookPixel';
import { UserSelectorContextProvider } from 'hooks/useUser';
import { LanguageContextProvider } from '@ui/hooks/useLanguage';
import App from './App';
import GlobalStyles from './global-styles';
import reportWebVitals from './reportWebVitals';
import { AlertsContextProvider } from './hooks/useAlerts';
import { FlagsContextProvider } from './hooks/useFlags';
import { LazyLDProvider } from './hooks/useLazyLaunchDarkly';

import '@ui/styles/global.css';
// Bootstrap CSS only - JS not needed (react-bootstrap handles interactivity)
import 'bootstrap/dist/css/bootstrap.min.css';
import { theme } from './theme';

import './i18n/config';
import './configureAxios';
import { HotjarContextProvider } from '@ui/hooks/useHotjar';
import { TrackingContextProvider } from '@ui/hooks/useTracking';

// Fonts are loaded via CSS @font-face in global.css (no need to import here)

import '@ui/components/styles.css';
import '@ui/components/Colors/style.css';
import '@ui/components/Fonts/style.css';
import NetworkStatus from '@ui/components/NetworkStatus';
import { PostHogProvider } from '@posthog/react';
import posthog from 'posthog-js';
import { schedulePostHogInit } from './utils/deferredAnalytics';

const root = ReactDOM.createRoot(document.getElementById('root'));

// !Due to the webpack5 migration and the polyfills required to have process.env
// !use REACT_APP_ENV instead on NODE_ENV

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false
    }
  }
});

library.add(faHandHoldingUsd, faCreditCard, faLandmark, faUserCircle, faArrowRightArrowLeft);

if (process.env.REACT_APP_ENV === 'prod') {
  Sentry.init({
    dsn: 'https://e0ecc61b81e776816938a8cffb6dab30@o4508227830022144.ingest.de.sentry.io/4508291747610704',
    integrations: [],
    tracesSampleRate: 1.0,
    environment: process.env.REACT_APP_ENV
  });
}

// Defer PostHog initialization to improve initial page load performance
// PostHog will be initialized after first paint via requestIdleCallback
schedulePostHogInit();

root.render(
  <>
    <GlobalStyles />
    <PostHogProvider client={posthog}>
      <BrowserRouter>
        <LanguageContextProvider>
          <GTMContextProvider>
            <CustomerIoContextProvider>
              <FacebookPixelContextProvider>
                <HotjarContextProvider>
                  <TrackingContextProvider>
                    <LazyLDProvider>
                      <UserSelectorContextProvider>
                        <WebSocketContextProvider>
                          <FlagsContextProvider>
                            <QueryClientProvider client={queryClient}>
                              <ThemeProvider theme={theme}>
                                <AlertsContextProvider>
                                  <NetworkStatus>
                                    <App />
                                  </NetworkStatus>
                                </AlertsContextProvider>
                              </ThemeProvider>
                            </QueryClientProvider>
                          </FlagsContextProvider>
                        </WebSocketContextProvider>
                      </UserSelectorContextProvider>
                    </LazyLDProvider>
                  </TrackingContextProvider>
                </HotjarContextProvider>
              </FacebookPixelContextProvider>
            </CustomerIoContextProvider>
          </GTMContextProvider>
        </LanguageContextProvider>
      </BrowserRouter>
    </PostHogProvider>
  </>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
